import { useState, useMemo, useRef } from "react";
import { Layout } from "@/components/Layout";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import {
  TRANSFORMATIONS,
  OBJECTIVE_LABELS,
  computeGlobalStats,
  computeAllCategoryStats,
} from "@/data/transformations";
import { CONTACT } from "@/lib/contact";
import {
  Star, Trophy, Fire, Heart, Medal, X, Calendar, Scales,
  Barbell, ArrowsClockwise, Lightning, ArrowRight, ChartLineUp, Clock,
} from "@phosphor-icons/react";

const OBJECTIVE_ICON_MAP = {
  fat_loss: Fire,
  muscle_gain: Barbell,
  recomposition: ArrowsClockwise,
  performance: Lightning,
  wellness: Heart,
};

const FILTERS = [
  { id: "all", label: "Todos", match: () => true },
  { id: "fat_loss", label: "Pérdida de grasa", match: (t) => t.objective === "fat_loss" },
  { id: "muscle_gain", label: "Ganancia muscular", match: (t) => t.objective === "muscle_gain" },
  { id: "recomposition", label: "Recomposición", match: (t) => t.objective === "recomposition" },
  { id: "performance", label: "Rendimiento", match: (t) => t.objective === "performance" },
  { id: "wellness", label: "Bienestar", match: (t) => t.objective === "wellness" },
  { id: "female", label: "Mujeres", match: (t) => t.gender === "female" },
  { id: "male", label: "Hombres", match: (t) => t.gender === "male" },
];

export default function SuccessStories() {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const galleryRef = useRef(null);

  const STATS = useMemo(() => computeGlobalStats(), []);
  const CATEGORIES = useMemo(() => computeAllCategoryStats(), []);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    return TRANSFORMATIONS.filter(f.match);
  }, [filter]);

  const ctaText = (t) =>
    `¡Hola AS TRAINING! Vi la transformación de ${t.name} en su web y quiero lograr un resultado similar (${OBJECTIVE_LABELS[t.objective].toLowerCase()}). ¿Cómo empiezo?`;

  const jumpToGallery = (objective) => {
    setFilter(objective);
    setTimeout(() => {
      galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-as-black border-b border-as-border as-grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <p className="as-label text-as-green mb-4">⭐ HISTORIAS DE ÉXITO</p>
          <h1 className="as-heading text-5xl sm:text-6xl md:text-7xl text-white mb-5" data-testid="stories-title">
            RESULTADOS <span className="text-as-green">REALES.</span><br />
            PERSONAS <span className="text-as-green">REALES.</span>
          </h1>
          <p className="text-as-gray max-w-2xl text-lg">
            Descubre cómo nuestros clientes transformaron su cuerpo y mejoraron su calidad de vida con AS TRAINING.
          </p>
        </div>
      </section>

      {/* CREDIBILITY STRIP */}
      <section className="bg-as-surface border-b border-as-border" data-testid="credibility-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-as-border -m-px">
          <StatBlock icon={Trophy} value={`+${STATS.clients}`} label="Clientes transformados" testid="stat-clients" />
          <StatBlock icon={Calendar} value={`${STATS.years_experience}+`} label="Años de experiencia" testid="stat-years" />
          <StatBlock icon={Medal} value={`${STATS.rating.toFixed(1)}/5`} label="Valoración promedio" testid="stat-rating" />
          <StatBlock icon={Heart} value={`${STATS.satisfaction_pct}%`} label="Satisfacción" testid="stat-satisfaction" />
          <StatBlock icon={ChartLineUp} value={`${STATS.renewal_rate_pct}%`} label="Renovación de membresía" testid="stat-renewal" />
        </div>
      </section>

      {/* RESULTADOS COMPROBADOS — clickable categories */}
      <section className="bg-as-black py-16 border-b border-as-border" data-testid="proven-results">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-3xl">
            <p className="as-label text-as-green mb-3">RESULTADOS COMPROBADOS</p>
            <h2 className="as-heading text-3xl sm:text-4xl lg:text-5xl text-white">
              CADA OBJETIVO. <span className="text-as-green">CADA HISTORIA.</span>
            </h2>
            <p className="text-as-gray mt-4">
              Encuentra a personas como tú que ya lograron su transformación. Haz clic en cualquier
              categoría para ver las historias completas.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map((cat) => (
              <CategoryCard key={cat.objective} cat={cat} onClick={() => jumpToGallery(cat.objective)} />
            ))}
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="bg-as-black sticky top-16 z-30 border-b border-as-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
                filter === f.id
                  ? "bg-as-green text-as-black"
                  : "bg-as-surface text-white border border-as-border hover:border-as-green"
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section ref={galleryRef} className="bg-as-surface py-12" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-as-gray">
              <p>No hay transformaciones con este filtro todavía.</p>
            </div>
          ) : filter === "all" ? (
            <div className="space-y-14">
              <GenderGroup
                title="HISTORIAS DE MUJERES"
                items={filtered.filter((t) => t.gender === "female")}
                onOpen={setSelected}
                ctaText={ctaText}
                testid="group-female"
              />
              <GenderGroup
                title="HISTORIAS DE HOMBRES"
                items={filtered.filter((t) => t.gender === "male")}
                onOpen={setSelected}
                ctaText={ctaText}
                testid="group-male"
              />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((t) => (
                <TransformationCard
                  key={t.id}
                  t={t}
                  onOpen={() => setSelected(t)}
                  ctaText={ctaText(t)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MODAL */}
      {selected && (
        <DetailModal
          t={selected}
          ctaText={ctaText(selected)}
          onClose={() => setSelected(null)}
        />
      )}
    </Layout>
  );
}

const CategoryCard = ({ cat, onClick }) => {
  const label = OBJECTIVE_LABELS[cat.objective];
  const Icon = OBJECTIVE_ICON_MAP[cat.objective] || Fire;
  const isEmpty = cat.count === 0;
  const featured = cat.featured;
  const posterImg = featured?.poster_url || featured?.after_url;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isEmpty}
      className={`text-left group bg-as-surface border transition-colors relative overflow-hidden ${
        isEmpty
          ? "border-as-border cursor-not-allowed opacity-70"
          : "border-as-border hover:border-as-green"
      }`}
      data-testid={`category-${cat.objective}`}
    >
      {/* Featured poster background (if any) */}
      {posterImg && !isEmpty && (
        <div className="absolute inset-0 opacity-25 group-hover:opacity-40 transition-opacity">
          <img
            src={posterImg}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "grayscale(30%) brightness(0.7)" }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-as-surface via-as-surface/85 to-as-surface/40" />
        </div>
      )}

      <div className="relative p-6 flex flex-col h-full min-h-[280px]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-as-black border border-as-green/40 flex items-center justify-center flex-shrink-0">
            <Icon size={22} weight="duotone" color="#39FF14" />
          </div>
          <div className="text-right">
            <p className="text-4xl font-black text-as-green leading-none">{cat.count}</p>
            <p className="text-[9px] uppercase tracking-widest text-as-gray mt-1">
              {cat.count === 1 ? "caso" : "casos"}
            </p>
          </div>
        </div>

        <h3 className="as-heading text-xl text-white mb-4 leading-tight">{label.toUpperCase()}</h3>

        {isEmpty ? (
          <>
            <p className="text-xs text-as-gray flex-1 leading-relaxed">
              Próximas historias en esta categoría. Sé uno de los primeros con AS TRAINING.
            </p>
            <span className="inline-flex items-center gap-1 mt-4 text-[10px] uppercase tracking-widest text-as-gray">
              <Clock size={12} weight="bold" /> Próximamente
            </span>
          </>
        ) : (
          <>
            {/* Featured case snippet */}
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-widest text-as-green mb-1">
                CASO DESTACADO · {featured.name.toUpperCase()}
              </p>
              <p className="text-sm text-white font-bold mb-1">{featured.key_result}</p>
              <p className="text-xs text-as-gray italic leading-relaxed line-clamp-3">
                &ldquo;{featured.testimonial.slice(0, 110)}
                {featured.testimonial.length > 110 ? "…" : ""}&rdquo;
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4 mt-auto">
              <MiniStat label="Tiempo promedio" value={`${cat.avg_weeks} sem`} />
              <MiniStat label="Valoración" value={`${featured.rating}.0/5`} />
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-as-border">
              <span className="text-[10px] uppercase tracking-widest text-as-gray group-hover:text-as-green transition-colors">
                Ver {cat.count === 1 ? "historia completa" : `las ${cat.count} historias`}
              </span>
              <ArrowRight size={16} weight="bold" color="#39FF14" />
            </div>
          </>
        )}
      </div>
    </button>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="bg-as-black/60 border border-as-border p-2">
    <p className="text-[9px] uppercase tracking-widest text-as-gray leading-tight">{label}</p>
    <p className="text-sm text-white font-bold mt-1">{value}</p>
  </div>
);

const GenderGroup = ({ title, items, onOpen, ctaText, testid }) => {
  if (items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-[2px] w-8 bg-as-green" />
        <h2 className="as-heading text-2xl sm:text-3xl text-white">
          {title}
        </h2>
        <span className="text-xs text-as-gray uppercase tracking-widest ml-auto">
          {items.length} {items.length === 1 ? "caso" : "casos"}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((t) => (
          <TransformationCard
            key={t.id}
            t={t}
            onOpen={() => onOpen(t)}
            ctaText={ctaText(t)}
          />
        ))}
      </div>
    </div>
  );
};

const StatBlock = ({ icon: Icon, value, label, testid }) => (
  <div className="bg-as-black p-6 text-center" data-testid={testid}>
    <Icon size={32} weight="duotone" color="#39FF14" className="mx-auto mb-2" />
    <p className="as-heading text-3xl sm:text-4xl text-white">{value}</p>
    <p className="as-label text-[10px] mt-1">{label}</p>
  </div>
);

const TransformationCard = ({ t, onOpen, ctaText }) => (
  <div
    className="bg-as-black border border-as-border hover:border-as-green transition-colors flex flex-col"
    data-testid={`transformation-${t.id}`}
  >
    <div
      className={`${t.poster_url ? "aspect-[4/5]" : "aspect-[4/3]"} relative cursor-pointer bg-as-black`}
      onClick={onOpen}
    >
      {t.poster_url ? (
        <img
          src={t.poster_url}
          alt={`Transformación de ${t.name}`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          data-testid={`poster-${t.id}`}
        />
      ) : (
        <BeforeAfterSlider before={t.before_url} after={t.after_url} testid={`slider-${t.id}`} />
      )}
      {t.video_url && (
        <div
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-as-green text-as-black px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest shadow-lg"
          data-testid={`video-badge-${t.id}`}
          aria-label="Testimonio en video disponible"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
          Video
        </div>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <div className="flex items-center justify-between mb-1">
        <h3 className="as-heading text-2xl text-white">{t.name}</h3>
        <span className="as-label text-as-green text-[10px]">{OBJECTIVE_LABELS[t.objective]}</span>
      </div>
      <div className="flex gap-1 mb-3" role="img" aria-label={`${t.rating} de 5 estrellas`}>
        {Array.from({ length: t.rating }, (_, i) => (
          <Star key={`star-${t.id}-${i}`} size={14} weight="fill" color="#39FF14" />
        ))}
      </div>
      <ul className="space-y-1 mb-3 text-sm text-white">
        {t.highlights.map((h) => (
          <li key={h} className="flex items-start gap-2">
            <span className="text-as-green">▸</span>
            <span>{h}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-as-gray italic mb-5 flex-1">
        &ldquo;{t.testimonial}&rdquo;
      </p>
      <a
        href={`https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(ctaText)}`}
        target="_blank"
        rel="noreferrer"
        className="as-btn-primary w-full !py-2 !text-[11px]"
        data-testid={`cta-${t.id}`}
      >
        🔥 QUIERO UN RESULTADO ASÍ
      </a>
    </div>
  </div>
);

const DetailModal = ({ t, ctaText, onClose }) => {
  const diff = t.weight_after - t.weight_before;
  const diffSign = diff > 0 ? "+" : "";
  const hasVideo = !!t.video_url;
  const [view, setView] = useState(hasVideo ? "video" : "poster");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="detail-modal"
    >
      <div
        className="bg-as-black border-2 border-as-green max-w-4xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-as-border flex justify-between items-start gap-4">
          <div>
            <p className="as-label text-as-green mb-1">{OBJECTIVE_LABELS[t.objective]} · {t.plan_name}</p>
            <h2 className="as-heading text-3xl text-white">{t.name.toUpperCase()}</h2>
            <p className="text-xs text-as-gray mt-1">{t.age} años · {t.gender === "female" ? "Mujer" : "Hombre"}</p>
          </div>
          <button onClick={onClose} className="text-white" data-testid="modal-close" aria-label="Cerrar">
            <X size={24} weight="bold" />
          </button>
        </div>

        {hasVideo && (
          <div
            className="flex border-b border-as-border"
            role="tablist"
            aria-label="Tipo de contenido"
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === "video"}
              onClick={() => setView("video")}
              className={`flex-1 px-4 py-3 text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 transition-colors ${
                view === "video" ? "bg-as-green text-as-black" : "text-white hover:text-as-green"
              }`}
              data-testid={`modal-tab-video-${t.id}`}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
              Testimonio en Video
            </button>
            {t.poster_url && (
              <button
                type="button"
                role="tab"
                aria-selected={view === "poster"}
                onClick={() => setView("poster")}
                className={`flex-1 px-4 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${
                  view === "poster" ? "bg-as-green text-as-black" : "text-white hover:text-as-green"
                }`}
                data-testid={`modal-tab-poster-${t.id}`}
              >
                Antes / Después
              </button>
            )}
          </div>
        )}

        {view === "video" && hasVideo ? (
          <div className="bg-as-black flex items-center justify-center" style={{ maxHeight: "70vh" }}>
            <video
              key={t.video_url}
              src={t.video_url}
              poster={t.poster_url || undefined}
              controls
              playsInline
              preload="metadata"
              className="w-full max-h-[70vh] object-contain bg-black"
              data-testid={`modal-video-${t.id}`}
            >
              Tu navegador no soporta la reproducción de video.
            </video>
          </div>
        ) : (
          <div className={t.poster_url ? "aspect-[4/5] sm:aspect-[3/4]" : "aspect-[16/10] sm:aspect-[16/9]"}>
            {t.poster_url ? (
              <img
                src={t.poster_url}
                alt={`Transformación de ${t.name}`}
                className="w-full h-full object-contain bg-as-black"
                data-testid={`modal-poster-${t.id}`}
              />
            ) : (
              <BeforeAfterSlider before={t.before_url} after={t.after_url} testid={`modal-slider-${t.id}`} />
            )}
          </div>
        )}

        <div className="p-6 grid sm:grid-cols-3 gap-4 border-b border-as-border">
          <DetailStat icon={Scales} label="Peso inicial" value={`${t.weight_before} ${t.weight_unit}`} />
          <DetailStat icon={Scales} label="Peso final" value={`${t.weight_after} ${t.weight_unit}`} highlight />
          <DetailStat icon={Calendar} label="Duración" value={`${t.duration_weeks} semanas`} />
        </div>

        <div className="p-6">
          <p className="as-label text-as-green mb-3">DIFERENCIA</p>
          <p className="as-heading text-4xl text-white mb-6">
            {diffSign}{diff} {t.weight_unit}
            <span className="text-base text-as-gray ml-2">en {t.duration_weeks} semanas</span>
          </p>

          <p className="as-label text-as-green mb-3">TESTIMONIO</p>
          <blockquote className="border-l-2 border-as-green pl-4 text-as-gray italic mb-6">
            &ldquo;{t.testimonial}&rdquo;
          </blockquote>

          <a
            href={`https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(ctaText)}`}
            target="_blank"
            rel="noreferrer"
            className="as-btn-primary w-full"
            data-testid={`modal-cta-${t.id}`}
          >
            🔥 QUIERO UN RESULTADO COMO ESTE
          </a>
        </div>
      </div>
    </div>
  );
};

const DetailStat = ({ icon: Icon, label, value, highlight }) => (
  <div className="as-card !p-4">
    <Icon size={20} weight="duotone" color="#39FF14" className="mb-2" />
    <p className="as-label text-[10px] mb-1">{label}</p>
    <p className={`as-heading text-2xl ${highlight ? "text-as-green" : "text-white"}`}>{value}</p>
  </div>
);
